/**
 * Custom-built service card visuals — feel like motion graphics, no stock photos.
 * Web / Mobile / API — each tells its own story with subtle 3D animation.
 */

/* ============================
   WEB VISUAL — animated dashboard inside browser frame
   ============================ */
export function WebVisual() {
  return (
    <div className="visual-stage" aria-hidden="true">
      <div className="web-window">
        <div className="web-window-bar">
          <i style={{ background: "#ff5e57" }} />
          <i style={{ background: "#ffbe3b" }} />
          <i style={{ background: "#10b981" }} />
          <span className="ml-2 text-[9px] text-[#94a3b8] font-mono-d">app.arjteam.io</span>
        </div>

        <div className="relative h-[140px] flex">
          {/* Sidebar */}
          <div className="w-[42px] border-r border-[#eef0f5] bg-[#fbfbfd] py-2.5 flex flex-col items-center gap-2">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="w-5 h-5 rounded-md"
                style={{
                  background: i === 1 ? "linear-gradient(135deg,#0066ff,#10b981)" : "#eef1f6",
                }}
              />
            ))}
          </div>

          {/* Main area */}
          <div className="flex-1 p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="h-2.5 w-16 rounded bg-[#0a0e1a]" />
              <div className="h-2 w-10 rounded bg-[#10b981]/30" />
            </div>

            <div className="flex items-end gap-1.5 h-[58px]">
              {[55, 38, 78, 64, 92, 70, 84, 60].map((h, i) => (
                <div
                  key={`bar-${i}`}
                  className="flex-1 rounded-t-sm bg-gradient-to-t from-[#0066ff]/25 to-[#0066ff] web-bar"
                  style={{ height: `${h}%`, animationDelay: `${i * 0.18}s` }}
                />
              ))}
            </div>

            {/* Pop card overlay */}
            <div
              className="absolute right-3 top-9 web-card-pop bg-white border border-[#e6ecff] rounded-md px-2.5 py-1.5 shadow-[0_8px_18px_-6px_rgba(10,15,30,0.18)]"
              style={{ animationDelay: "0.5s" }}
            >
              <div className="text-[8px] font-mono-d uppercase text-[#10b981] font-bold tracking-wider">+24%</div>
              <div className="text-[10px] font-display font-bold text-[#0a0e1a] -mt-0.5">$48.2k</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================
   MOBILE VISUAL — three floating phones with cycling app screens + notifications
   ============================ */
export function MobileVisual() {
  return (
    <div className="visual-stage mobile-stage" aria-hidden="true">
      <div className="phone-frame phone-a">
        <div className="phone-screen" style={{ animationDelay: "-2s" }} />
        <div className="phone-screen-ui">
          <div className="pl-bar" /><div className="pl-bar short" />
          <div className="pl-card" />
          <div className="pl-row"><span className="pl-dot" /><span className="pl-line" /></div>
        </div>
      </div>
      <div className="phone-frame phone-b">
        <div className="phone-screen" />
        <div className="phone-screen-ui">
          <div className="pl-bar" /><div className="pl-bar short" />
          <div className="pl-card" />
          <div className="pl-row"><span className="pl-dot" /><span className="pl-line" /></div>
          <div className="pl-row"><span className="pl-dot" /><span className="pl-line" /></div>
        </div>
      </div>
      <div className="phone-frame phone-c">
        <div className="phone-screen" style={{ animationDelay: "-4s" }} />
        <div className="phone-screen-ui">
          <div className="pl-bar" /><div className="pl-bar short" />
          <div className="pl-card" />
          <div className="pl-row"><span className="pl-dot" /><span className="pl-line" /></div>
        </div>
      </div>

      {/* Floating notifications */}
      <div className="notif" style={{ top: "16%", animationDelay: "0s" }}>
        🔔 New order
      </div>
      <div className="notif" style={{ top: "26%", left: "30%", animationDelay: "1.2s" }}>
        💬 Reply from user
      </div>
      <div className="notif" style={{ top: "20%", left: "72%", animationDelay: "2.4s" }}>
        ✓ Sync complete
      </div>
    </div>
  );
}

/* ============================
   API VISUAL — animated wires connecting nodes (data flow)
   ============================ */
export function ApiVisual() {
  return (
    <div className="visual-stage" aria-hidden="true">
      <svg
        viewBox="0 0 400 220"
        className="absolute inset-0 w-full h-full"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <linearGradient id="api-grad-a" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#0066ff" />
            <stop offset="100%" stopColor="#10b981" />
          </linearGradient>
          <linearGradient id="api-grad-b" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#7c3aed" />
            <stop offset="100%" stopColor="#0066ff" />
          </linearGradient>
          <radialGradient id="api-bg-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(0,102,255,0.12)" />
            <stop offset="100%" stopColor="rgba(0,102,255,0)" />
          </radialGradient>
          <filter id="api-shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#0a0e1a" floodOpacity="0.18" />
          </filter>
        </defs>

        {/* Background glow */}
        <rect x="0" y="0" width="400" height="220" fill="url(#api-bg-glow)" />

        {/* Animated wires */}
        <path className="wire wire-a" d="M70 110 C 130 60, 200 60, 260 110" />
        <path className="wire wire-b" d="M70 110 C 140 170, 200 170, 260 110" />
        <path className="wire wire-c" d="M260 110 C 300 70, 330 70, 350 50" />
        <path className="wire wire-a" d="M260 110 C 300 150, 330 150, 350 170" style={{ animationDelay: "0.4s" }} />

        {/* Data packets travelling along wires */}
        <circle r="3.5" fill="#10b981">
          <animateMotion dur="3s" repeatCount="indefinite" path="M70 110 C 130 60, 200 60, 260 110" />
        </circle>
        <circle r="3.5" fill="#0066ff">
          <animateMotion dur="3.6s" repeatCount="indefinite" path="M70 110 C 140 170, 200 170, 260 110" />
        </circle>
        <circle r="3" fill="#7c3aed">
          <animateMotion dur="2.4s" repeatCount="indefinite" path="M260 110 C 300 70, 330 70, 350 50" />
        </circle>

        {/* Source node — left */}
        <g className="api-node api-node-pulse" style={{ transformOrigin: "70px 110px" }}>
          <circle cx="70" cy="110" r="22" fill="#ffffff" stroke="url(#api-grad-a)" strokeWidth="2" />
          <text x="70" y="115" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="11" fontWeight="700" fill="#0a0e1a">
            API
          </text>
        </g>

        {/* Hub node — center */}
        <g className="api-node">
          <circle cx="260" cy="110" r="28" fill="url(#api-grad-a)" filter="url(#api-shadow)" />
          <path d="M250 110 L256 116 L272 100" stroke="#fff" strokeWidth="2.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>

        {/* Destination nodes — right */}
        <g className="api-node">
          <rect x="335" y="35" width="40" height="32" rx="8" fill="#ffffff" stroke="#7c3aed" strokeWidth="2" />
          <text x="355" y="56" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="9" fontWeight="700" fill="#7c3aed">
            DB
          </text>
        </g>
        <g className="api-node">
          <rect x="335" y="155" width="40" height="32" rx="8" fill="#ffffff" stroke="#0066ff" strokeWidth="2" />
          <text x="355" y="176" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="9" fontWeight="700" fill="#0066ff">
            UI
          </text>
        </g>

        {/* Floating service chips — repositioned to avoid badge overlap */}
        <g opacity="0.92">
          <rect x="22" y="14" width="64" height="22" rx="6" fill="#ffffff" stroke="#e6ecff" />
          <circle cx="34" cy="25" r="3" fill="#10b981" />
          <text x="44" y="29" fontFamily="JetBrains Mono" fontSize="10" fontWeight="700" fill="#0a0e1a">
            Stripe
          </text>
        </g>
        <g opacity="0.92">
          <rect x="106" y="14" width="76" height="22" rx="6" fill="#ffffff" stroke="#e6ecff" />
          <circle cx="118" cy="25" r="3" fill="#0066ff" />
          <text x="128" y="29" fontFamily="JetBrains Mono" fontSize="10" fontWeight="700" fill="#0a0e1a">
            OpenAI
          </text>
        </g>
        <g opacity="0.92">
          <rect x="200" y="14" width="74" height="22" rx="6" fill="#ffffff" stroke="#e6ecff" />
          <circle cx="212" cy="25" r="3" fill="#7c3aed" />
          <text x="222" y="29" fontFamily="JetBrains Mono" fontSize="10" fontWeight="700" fill="#0a0e1a">
            Twilio
          </text>
        </g>
      </svg>

      {/* Status label */}
      <div className="absolute bottom-2 right-3 text-[10px] font-mono-d uppercase tracking-wider text-[#10b981] font-bold flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-[#10b981] pulse-dot" />
        Streaming
      </div>
    </div>
  );
}
